import { Context, type Effect, type Queue, type Scope } from "effect";
import type { Atom, AsyncResult } from "effect/unstable/reactivity";
import type { ResourceFailure } from "@workerstation/shared/errors/resource";
import type { RrwebReplayEvent } from "@workerstation/shared/flow-recording-player-model";
import type { FlowRecordingSegmentSummary } from "@workerstation/shared/flow-recording-model";
export type RecordingReplayEvent = {
    readonly _tag: "Tick";
} | {
    readonly _tag: "Finish";
} | {
    readonly _tag: "BoxResize";
} | {
    readonly _tag: "Resize";
    readonly payload: unknown;
};
export interface RecordingReplayEngine {
    readonly events: Queue.Queue<RecordingReplayEvent>;
    readonly currentTime: Effect.Effect<number, ResourceFailure>;
    readonly goto: (offset: number, play?: boolean) => Effect.Effect<void, ResourceFailure>;
    readonly play: Effect.Effect<void, ResourceFailure>;
    readonly pause: Effect.Effect<void, ResourceFailure>;
    readonly setSpeed: (speed: number) => Effect.Effect<void, ResourceFailure>;
    readonly resize: (payload: unknown) => Effect.Effect<void, ResourceFailure>;
    readonly refit: Effect.Effect<void, ResourceFailure>;
}
export class RecordingReplayIo extends Context.Service<RecordingReplayIo, {
    readonly gunzip: (bytes: Uint8Array) => Effect.Effect<Uint8Array, ResourceFailure>;
    readonly frame: Effect.Effect<number, ResourceFailure>;
    readonly now: Effect.Effect<number, ResourceFailure>;
    readonly mount: (events: readonly RrwebReplayEvent[], target: HTMLElement, active: () => boolean) => Effect.Effect<RecordingReplayEngine, ResourceFailure, Scope.Scope>;
}>()("workerstation/frontend/RecordingReplayIo") {
}
export interface RecordingReplayView {
    readonly status: "loading" | "ready" | "error";
    readonly message: string | null;
    readonly baseMs: number;
    readonly closed: boolean;
}
export interface RecordingReplaySession {
    readonly snapshot: Atom.Atom<AsyncResult.AsyncResult<RecordingReplayView>>;
    readonly read: Effect.Effect<RecordingReplayView>;
    readonly goto: (offset: number, play?: boolean) => Effect.Effect<void>;
    readonly play: Effect.Effect<void>;
    readonly pause: Effect.Effect<void>;
    readonly setSpeed: (speed: number) => Effect.Effect<void>;
}
export interface RecordingReplayInput {
    readonly workspaceId: string;
    readonly recordingId: string;
    readonly segments: readonly FlowRecordingSegmentSummary[];
    readonly hasVideo: boolean;
    readonly initialPlayheadMs: number;
}
export class RecordingReplaySessions extends Context.Service<RecordingReplaySessions, {
    readonly open: (input: RecordingReplayInput, target: HTMLElement, onPlayhead: (absoluteMs: number) => Effect.Effect<void>) => Effect.Effect<RecordingReplaySession, never, Scope.Scope>;
}>()("workerstation/frontend/RecordingReplaySessions") {
}
